import { QueryClient } from '@tanstack/react-query';
import { ApiError, NetworkError } from '../../infrastructure/api/ApiError';

/** How many times a save is re-sent after its first failure. */
const MAX_MUTATION_RETRIES = 2;

/**
 * Statuses where the server says it did NOT process the write and asks for it later: rate-limited
 * (429) and unavailable (503).
 */
const NOT_PROCESSED_STATUSES = new Set([429, 503]);

/**
 * sprint-277/S003/D4 (PO) — whether a failed SAVE is sent again. The phone outbox's policy, on the
 * web (MobileApp/src/domain/outbox/drain.ts).
 *
 * 🔴 A save is re-sent only when the server provably never took it:
 * - no response at all (NetworkError);
 * - 429 or 503, where the server says it did not process the request.
 *
 * Never re-sent:
 * - a 4xx refusal (400/403/404/409/412/422). The answer will not change, and re-sending a 409
 *   only repeats the conflict. A 401 is the ApiClient refresh interceptor's, not this.
 * - 500, 502, 504 or a timeout. The server may already have applied the write, and these POSTs
 *   are not idempotent yet, so a re-send could assign the same task twice. The screen keeps what
 *   was typed and says the save failed; the person decides.
 *
 * ⚠️ A NetworkError can, rarely, mean the connection dropped after the server received the request.
 * Accepted for now; idempotency keys on the create endpoints (on the register) close that too, and
 * then 500/502/504/timeouts can be retried as well.
 *
 * Nothing typed is lost either way: data survives because screens clear their forms only on
 * success, not because a request is repeated.
 */
export function shouldRetryMutation(failureCount: number, error: unknown): boolean {
  if (failureCount >= MAX_MUTATION_RETRIES) {
    return false;
  }
  if (NetworkError.isNetworkError(error)) {
    return true;
  }
  return ApiError.isApiError(error) && NOT_PROCESSED_STATUSES.has(error.statusCode);
}

/** 1 s, then 2 s, each with up to 25% jitter so a burst of failed saves does not re-arrive together. */
export function mutationRetryDelay(attemptIndex: number): number {
  const base = Math.min(1000 * 2 ** attemptIndex, 2000);
  return base + Math.floor(Math.random() * base * 0.25);
}

/**
 * Cache configuration constants (in milliseconds)
 *
 * These values define how long data is considered fresh (staleTime)
 * and how long it stays in cache before garbage collection (gcTime)
 */
export const CACHE_CONFIG = {
  /**
   * User data cache: 5 minutes
   * - Moderately stable data
   * - Updated on profile changes
   */
  USER: {
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 10 // 10 minutes
  },

  /**
   * Mention data cache: 1 minute
   * - Frequently changing data
   * - New mentions arrive regularly
   */
  MENTIONS: {
    staleTime: 1000 * 60, // 1 minute
    gcTime: 1000 * 60 * 5 // 5 minutes
  },

  /**
   * Business data cache: 10 minutes
   * - Moderately stable data
   * - Changes are infrequent
   */
  BUSINESSES: {
    staleTime: 1000 * 60 * 10, // 10 minutes
    gcTime: 1000 * 60 * 30 // 30 minutes
  },

  /**
   * Region data cache: 30 minutes
   * - Very stable data
   * - Admin-managed, rarely changes
   */
  REGIONS: {
    staleTime: 1000 * 60 * 30, // 30 minutes
    gcTime: 1000 * 60 * 60 // 1 hour
  },

  /**
   * Search results cache: 30 seconds
   * - Transient data
   * - User-specific, context-dependent
   */
  SEARCH: {
    staleTime: 1000 * 30, // 30 seconds
    gcTime: 1000 * 60 * 2 // 2 minutes
  }
};

/**
 * Create and configure the React Query client
 *
 * Global defaults apply to all queries unless overridden
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Default staleTime: 1 minute
      staleTime: 1000 * 60,

      // Default gcTime (garbage collection): 5 minutes
      gcTime: 1000 * 60 * 5,

      // Retry failed requests once
      retry: 1,

      // Retry delay: exponential backoff (1s, 2s)
      retryDelay: (attemptIndex: number): number => Math.min(1000 * 2 ** attemptIndex, 2000),

      // Don't refetch on window focus (we have background refetch)
      refetchOnWindowFocus: false,

      // Don't refetch on mount if data is fresh
      refetchOnMount: false,

      // Refetch on reconnect (user's internet came back)
      refetchOnReconnect: true
    },
    mutations: {
      // sprint-277/S003/D4 — only when the server never took the write. See shouldRetryMutation.
      retry: shouldRetryMutation,
      retryDelay: mutationRetryDelay
    }
  }
});

/**
 * Export a typed version of useQueryClient for better TypeScript support
 */
export { useQueryClient } from '@tanstack/react-query';
